import {
	BusyIndicator,
	DateString,
	ListItem,
	PipeSeparator,
	Space
} from "client/Components";
import { DestinationWindow } from "client/Api/Apps/common";
import {
	AlertManager,
	useAlerts,
	useIsTouchDevice
} from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import { Guid } from "client_legacy/ndaw/guid";
import {
	DocumentsLiveInformationProvider,
	InterestedRecentDocumentsParty
} from "client_legacy/Workspace/LiveInformation/Documents/provider";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { LiveTileContent } from "../LiveTileContent";
import { TileRefresh } from "../TileRefresh/TileRefresh";
import { documentsConfiguration, userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { DimensionToRender } from "../../RenderState/common";
import { UserEditorPreference } from "client_legacy/Documents/officeDocumentsOpenPreference";
import { NewDocumentType } from "client/Api/Documents/common";
import { isNewDocumentDialogSupported, useNewDocumentDialog } from "client/Features/Documents/Dialogs/NewDocumentDialog";
import {
	formatFileLocation,
	getLocationModelFromDocument,
	SharePointItemLocationViewModel
} from "client/Features/Layout/TitleBar/GlobalSearch/Items/DocumentListItem";
import { TimeZone } from "client/DateAndTime/timezones";
import { useNoDocumentStoresConfiguredDialog } from "client/Features/Documents/NoDocumentStoresConfiguredDialog";
import { generateTrackingId, isNotNil } from "client/Misc/utils";
import { RecentDocumentItemViewModel } from "client/Api/Documents/SharePoint/common";
import {
	getOpenActions,
	getRecentDocumentSelectionActions,
	RecentDocumentAction,
	RecentDocumentActionContext,
	RecentDocumentActionHandlers
} from "client/Features/Documents/SharePoint/RecentlyModified/actions";
import { useEditorPreference } from "client/Features/Documents/useEditorPreference";
import { useImagePreviewDialog } from "client/Features/Documents/Dialogs/ImagePreview/ImagePreviewDialog";
import { convertRecentDocumentToImageFile } from "client/Features/Documents/SharePoint/RecentlyModified/SharePointRecentlyModified";
import {
	Action,
	ActionKind,
	reduceToAvailableActions
} from "client_legacy/ndaw/actions";
import { generateSharePointUniqueKey } from "client/Features/Documents/SharePoint/SharePointOverView";
import { AppImageDisplayInfo } from "client/Api/AppIcon/common";
import { DocumentListItem } from "./DocumentListItem";

export type RecentTabProps = {
	appIcon: AppImageDisplayInfo;
	tabSelector: ReactElement;
	tabId: Guid;
	destinationWindow: DestinationWindow;
	dimension: DimensionToRender;
	provider: DocumentsLiveInformationProvider;
	onNavigate: () => void;
	timezone: TimeZone;
	addAnalyticsEvent: () => void;
};

export const RecentTab = (props: RecentTabProps): ReactElement => {
	const {
		appIcon,
		tabSelector,
		tabId,
		destinationWindow,
		dimension,
		provider,
		onNavigate,
		timezone,
		addAnalyticsEvent
	} = props;

	const [noDocumentStoresConfiguredDialog, openNoDocumentStoresConfiguredDialog] = useNoDocumentStoresConfiguredDialog();
	const [newDocumentDialog, openNewDocumentDialog] = useNewDocumentDialog();
	const [imagePreviewDialog, openImagePreviewDialog] = useImagePreviewDialog();
	const alertManager: AlertManager = useAlerts();
	const editorPreference: UserEditorPreference = useEditorPreference();
	const {
		sharePoint
	} = documentsConfiguration();

	const navigateHandler = useCallback(
		(): void => {
			addAnalyticsEvent();
			onNavigate();
		},
		[addAnalyticsEvent, onNavigate]
	);

	const newDocumentHandler = useCallback(
		(kind: NewDocumentType): void => {
			addAnalyticsEvent();

			if (isNewDocumentDialogSupported(kind)) {
				openNewDocumentDialog({ newDocumentType: kind });
			} else {
				const { isAdmin } = userConfiguration();

				if (isAdmin) {
					openNoDocumentStoresConfiguredDialog({ isForAdmin: true });
				} else {
					alertManager.showError(localize("_DocumentsIsNotConfiguredUserBody"));
				}
			}
		},
		[
			addAnalyticsEvent,
			alertManager,
			openNewDocumentDialog,
			openNoDocumentStoresConfiguredDialog
		]
	);

	const mainActions = useMemo(
		(): readonly Action<void>[] => {
			return listMainActions({
				new: newDocumentHandler
			});
		},
		[newDocumentHandler]
	);

	const selectionActionsHandlers = useMemo(
		(): RecentDocumentActionHandlers => ({
			openImageDialog: async (item: RecentDocumentItemViewModel): Promise<void> => {
				await openImagePreviewDialog({
					currentImage: convertRecentDocumentToImageFile(item),
					actions: [],
					context: undefined
				});
			}
		}),
		[openImagePreviewDialog]
	);

	const shouldShowQuickActions: boolean = dimension.kind === "normal"
		&& dimension.dimension
			.columnSpan > 4;

	const actions = useMemo(
		(): readonly RecentDocumentAction[] => {
			return getRecentDocumentSelectionActions({
				alertManager,
				editorPreference,
				handlers: selectionActionsHandlers
			}).map(
				(action: RecentDocumentAction): RecentDocumentAction => ({
					...action,
					execute: (context: RecentDocumentActionContext): void => {
						addAnalyticsEvent();
						action.execute(context);
					}
				})
			);
		},
		[
			addAnalyticsEvent,
			alertManager,
			editorPreference,
			selectionActionsHandlers
		]
	);

	const openDocumentHandler = useCallback(
		(actionContext: RecentDocumentActionContext): void => {
			addAnalyticsEvent();

			const actions: readonly RecentDocumentAction[] = getOpenActions({
				alertManager,
				editorPreference,
				handlers: selectionActionsHandlers
			});

			reduceToAvailableActions(actions, actionContext)[0]
				?.execute(actionContext);
		},
		[
			addAnalyticsEvent,
			alertManager,
			editorPreference,
			selectionActionsHandlers
		]
	);

	return (
		<>
			<LiveTileContent.Header
				icon={appIcon}
				actionContext={undefined}
				actions={mainActions}
			>
				{tabSelector}
			</LiveTileContent.Header>

			<LiveTileContent.Content>
				<RecentDocumentsContent
					tabId={tabId}
					dimension={dimension}
					provider={provider}
					timezone={timezone}
					onOpenItem={openDocumentHandler}
					destinationWindow={destinationWindow}
					actions={actions}
					isSharingViaLinkEnabled={sharePoint.isSharingViaLinkEnabled}
					shouldShowQuickActions={shouldShowQuickActions}
				/>
			</LiveTileContent.Content>

			<LiveTileContent.Footer
				name={localize("_Documents")}
				onClick={navigateHandler}
			/>

			{noDocumentStoresConfiguredDialog}
			{newDocumentDialog}
			{imagePreviewDialog}
		</>
	);
};
RecentTab.displayName = "RecentTab";

type RecentDocumentsContentProps = {
	tabId: Guid;
	dimension: DimensionToRender;
	provider: DocumentsLiveInformationProvider;
	timezone: TimeZone;
	onOpenItem: (actionContext: RecentDocumentActionContext) => void;
	destinationWindow: DestinationWindow;
	actions: readonly RecentDocumentAction[];
	isSharingViaLinkEnabled: boolean;
	shouldShowQuickActions: boolean;
};

const RecentDocumentsContent = (props: RecentDocumentsContentProps): ReactElement => {
	const {
		tabId,
		dimension,
		provider,
		timezone,
		actions,
		destinationWindow,
		isSharingViaLinkEnabled,
		shouldShowQuickActions,
		onOpenItem
	} = props;

	const [loadingState, setLoadingState] = useState<LoadingState>({
		isLoading: true
	});

	const isTouch: boolean = useIsTouchDevice();

	const shouldUseWideTemplate: boolean = dimension.kind === "normal"
		&& dimension.dimension
			.columnSpan > 4;

	useEffect(
		(): (() => void) => {
			const party: InterestedRecentDocumentsParty = {
				partyId: tabId,
				resourceId: "recent-documents",
				informAboutGlobalRetrievalFailure: (): void => {
					setLoadingState(({
						isLoading: false,
						hasGlobalFailure: true,
						isSharePointConfigured: true
					}));
				},
				informAboutConsecutiveErrors: (): void => {
					setLoadingState(({
						isLoading: false,
						hasTooManyConsecutiveErrors: true,
						isSharePointConfigured: true
					}));
				},
				informAboutSharePointNotBeingConfigured: () => {
					setLoadingState(({
						isSharePointConfigured: false,
						isLoading: false
					}));
				},
				update: (items): void => {
					setLoadingState(({
						isSharePointConfigured: true,
						isLoading: false,
						items
					}));
				}
			};

			setLoadingState(({
				isLoading: true
			}));

			provider.register(party, { items: true });

			return () => {
				provider.unregister(party);
			};
		},
		[
			provider,
			tabId
		]
	);

	const renderContent = (): ReactElement | null => {
		if (loadingState.isLoading) {
			return null;
		}

		if (!loadingState.isSharePointConfigured) {
			return (
				<LiveTileContent.Message
					text={localize("_TheConnectionWithSharePointIsNotConfiguredThereforeWeCouldNotRetrieveYourDocuments")}
				/>
			);
		}

		if (loadingState.hasGlobalFailure) {
			return (
				<LiveTileContent.Message
					text={localize("_WeCouldNotRetrieveTheRecentlyModifiedDocuments")}
				/>
			);
		}

		if (loadingState.hasTooManyConsecutiveErrors) {
			return (
				<TileRefresh onRefresh={() => provider.forceReload()} />
			);
		}

		const { items } = loadingState;

		if (items.length === 0) {
			return (
				<LiveTileContent.Message
					text={localize("_NoRecentlyModifiedDocuments")}
				/>
			);
		}

		return (
			<LiveTileContent.List trackingId="recent-documents">
				{items.map((x): ReactElement => (
					<RecentListItem
						key={generateSharePointUniqueKey(x.id, x.location)}
						trackingId={generateTrackingId("document", `|${x.id}`)}
						document={x}
						shouldUseWideTemplate={shouldUseWideTemplate}
						isTouch={isTouch}
						actions={actions}
						destinationWindow={destinationWindow}
						isSharingViaLinkEnabled={isSharingViaLinkEnabled}
						timezone={timezone}
						onOpenItem={onOpenItem}
						shouldShowQuickActions={shouldShowQuickActions}
					/>
				))}
			</LiveTileContent.List>
		);
	};

	return (
		<LiveTileContent.ContentStack>
			<LiveTileContent.BusyIndicatorContainer>
				{loadingState.isLoading && (
					<BusyIndicator />
				)}
				<LiveTileContent.Scrollable>
					{renderContent()}
				</LiveTileContent.Scrollable>
			</LiveTileContent.BusyIndicatorContainer>
		</LiveTileContent.ContentStack>
	);
};
RecentDocumentsContent.displayName = "RecentDocumentsContent";

type RecentListItemProps = {
	document: RecentDocumentItemViewModel;
	shouldUseWideTemplate: boolean;
	actions: readonly RecentDocumentAction[];
	isSharingViaLinkEnabled: boolean;
	destinationWindow: DestinationWindow;
	timezone: TimeZone;
	onOpenItem: (actionContext: RecentDocumentActionContext) => void;
	trackingId: string;
	isTouch: boolean;
	shouldShowQuickActions: boolean;
};

const RecentListItem = (props: RecentListItemProps): ReactElement => {
	const {
		document,
		shouldUseWideTemplate,
		actions,
		destinationWindow,
		isSharingViaLinkEnabled,
		timezone,
		trackingId,
		onOpenItem,
		isTouch,
		shouldShowQuickActions
	} = props;

	const locationModel = useMemo(
		(): SharePointItemLocationViewModel =>
			getLocationModelFromDocument(document),
		[document]
	);

	const actionContext = useMemo(
		(): RecentDocumentActionContext => ({
			isSharingViaLinkEnabled,
			shouldOpenInNewWindow: destinationWindow === DestinationWindow.NewWindow,
			isInSelectMode: false,
			isMobile: false,
			selection: [document]
		}),
		[
			destinationWindow,
			isSharingViaLinkEnabled,
			document
		]
	);

	const documentOpenHandler = useCallback(
		(): void => {
			onOpenItem(actionContext);
		},
		[actionContext, onOpenItem]
	);

	const renderAdditionalInformation = (): ReactElement => {
		return (
			<>
				<ListItem.SubTitleIcon kind="pen" />
				{isNotNil(document.modifiedOn) && (
					<>
						<Space />
						<DateString
							value={document.modifiedOn}
							format="RelativeTimestamp"
							timezone={timezone}
						/>
					</>
				)}
				{isNotNil(document.modifiedBy) && (
					<>
						<PipeSeparator />
						{localize("_(Edited)By").toLowerCase()}
						<Space />
						{document.modifiedBy}
					</>
				)}
				{shouldUseWideTemplate && locationModel.numberOfSegments > 0 && (
					<>
						<PipeSeparator />
						{formatFileLocation(locationModel)}
					</>
				)}
			</>
		);
	};

	return (
		<DocumentListItem
			trackingId={trackingId}
			documentKind={document.itemType}
			extension={document.extension}
			name={document.name}
			isShared={document.isShared}
			subtitle={renderAdditionalInformation()}
			actionContext={actionContext}
			actions={actions}
			onClick={documentOpenHandler}
			isTouch={isTouch}
			shouldShowQuickActions={shouldShowQuickActions}
		/>
	);
};
RecentListItem.displayName = "RecentListItem";

type LoadingState = {
	isLoading: true;
} | (
	{
		isLoading: false;
	}
	&
	(
		{
			isSharePointConfigured: false;
		}
		|
		(
			{
				isSharePointConfigured: true;
			}
			&
			(
				{
					hasTooManyConsecutiveErrors: true;
					hasGlobalFailure?: undefined;
					items?: undefined;
				}
				|
				{
					hasGlobalFailure: true;
					hasTooManyConsecutiveErrors?: undefined;
					items?: undefined;
				}
				|
				{
					items: readonly RecentDocumentItemViewModel[];
					hasGlobalFailure?: undefined;
					hasTooManyConsecutiveErrors?: undefined;
				}
			)
		)
	)
);

type MainActionHandlers = {
	new: (kind: NewDocumentType) => void;
};

function listMainActions(handlers: MainActionHandlers): readonly Action<void>[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "new-word",
			title: localize("_NewWordDocument"),
			icon: { kind: "svg", name: "docx" },
			canExecute: () => true,
			execute: () => handlers.new(NewDocumentType.Word)
		},
		{
			kind: ActionKind.Regular,
			name: "new-excel",
			title: localize("_NewExcelWorksheet"),
			icon: { kind: "svg", name: "xlsx" },
			canExecute: () => true,
			execute: () => handlers.new(NewDocumentType.Excel)
		},
		{
			kind: ActionKind.Regular,
			name: "new-powerpoint",
			title: localize("_NewPowerPointPresentation"),
			icon: { kind: "svg", name: "powerpoint" },
			canExecute: () => true,
			execute: () => handlers.new(NewDocumentType.PowerPoint)
		},
		{
			kind: ActionKind.Regular,
			name: "new-onenote",
			title: localize("_NewOneNoteNotebook"),
			icon: { kind: "svg", name: "onenote" },
			canExecute: () => true,
			execute: () => handlers.new(NewDocumentType.OneNote)
		}
	];
}

